import type { MessageKind } from '../types';
import { getSupabase } from './supabase';

export const AVATARS_BUCKET = 'avatars';
export const ATTACHMENTS_BUCKET = 'message-attachments';
export const CONVERSATION_ICONS_BUCKET = 'conversation-icons';

export function classifyFile(mimeType: string): MessageKind {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'file';
}

export function sanitizeFileName(fileName: string) {
  return fileName
    .normalize('NFKD')
    .replace(/[^\w.\-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 96);
}

export async function uploadMessageAttachment(conversationId: string, userId: string, file: File) {
  const client = getSupabase();
  const safeName = sanitizeFileName(file.name || 'attachment');
  const path = `${conversationId}/${userId}/${crypto.randomUUID()}-${safeName}`;

  const { error } = await client.storage.from(ATTACHMENTS_BUCKET).upload(path, file, {
    cacheControl: '3600',
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });

  if (error) throw error;

  return {
    attachment_path: path,
    attachment_name: file.name,
    attachment_type: file.type || 'application/octet-stream',
    attachment_size: file.size,
    kind: classifyFile(file.type || 'application/octet-stream'),
  };
}

export async function getSignedAttachmentUrl(path: string) {
  const client = getSupabase();
  const { data, error } = await client.storage
    .from(ATTACHMENTS_BUCKET)
    .createSignedUrl(path, 60 * 60);

  if (error) throw error;
  return data.signedUrl;
}

export async function uploadAvatar(userId: string, file: File) {
  const client = getSupabase();
  const extension = file.name.split('.').pop() || 'png';
  const path = `${userId}/avatar.${extension}`;

  const { error } = await client.storage.from(AVATARS_BUCKET).upload(path, file, {
    cacheControl: '3600',
    contentType: file.type || 'image/png',
    upsert: true,
  });

  if (error) throw error;

  const { data } = client.storage.from(AVATARS_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

export async function uploadConversationIcon(conversationId: string, file: File) {
  const client = getSupabase();
  const extension = file.name.split('.').pop() || 'png';
  const path = `${conversationId}/icon.${extension}`;

  const { error } = await client.storage.from(CONVERSATION_ICONS_BUCKET).upload(path, file, {
    cacheControl: '3600',
    contentType: file.type || 'image/png',
    upsert: true,
  });

  if (error) throw error;

  const { data } = client.storage.from(CONVERSATION_ICONS_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}
